import { useState, useEffect } from "react";
import axios from "axios";
import { Share } from "lucide-react";

import REFER1 from "../images/refer/refer1.png";
import REFER2 from "../images/refer/refer_1lyoptions.png";
import REFER3 from "../images/refer/refer3.png";

const ReferAndEarnModal = ({ isOpen, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [clientId, setClientId] = useState("unknown");
  const [cardHeading, setCardHeading] = useState(
    "₹1000/- for you per referral"
  );
  const [cardBody, setCardBody] = useState(
    "Share your experience of 1lyoptions with your trading buddies and refer us!"
  );
  const [shareContent, setShareContent] = useState(
    "Check out 1lyoptions by AliceBlue! Experience this amazing tool!"
  );
  const [showTerms, setShowTerms] = useState(false);

  const images = [REFER1, REFER2, REFER3];

  useEffect(() => {
    loadClientId();
    fetchReferAndEarnContent();

    // Auto-advance image slider
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const loadClientId = async () => {
    // Get userId from localStorage instead of SharedPreferences
    const userId = localStorage.getItem("user_id");
    setClientId(userId || "unknown");
  };

  const fetchReferAndEarnContent = async () => {
    try {
      const response = await axios.get(
        "https://p2qontpozf.execute-api.ap-south-1.amazonaws.com/default/referEarnContent"
      );

      if (response.status === 200 && response.data) {
        setCardHeading(response.data.card_heading || cardHeading);
        setCardBody(response.data.card_body || cardBody);
        setShareContent(response.data.share_content || shareContent);
      }
    } catch (error) {
      console.error("Failed to fetch content:", error);
    }
  };

  const shareLink = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: "Refer and Earn",
          text: `${shareContent} :\nhttps://ekyc.aliceblueonline.com/?source=${clientId}`,
          url: `https://ekyc.aliceblueonline.com/?source=${clientId}`,
        });
      } else {
        // Fallback for browsers that don't support Web Share API
        navigator.clipboard.writeText(
          `${shareContent} :\nhttps://ekyc.aliceblueonline.com/?source=${clientId}`
        );
        alert("Link copied to clipboard!");
      }
    } catch (error) {
      console.error("Error while sharing:", error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="modal-box w-[400px] h-[625px] max-h-[90vh] bg-modalBg rounded-lg overflow-auto">
        {!showTerms ? (
          <div className="flex flex-col">
            <div className="text-left">
              <h2 className="text-xl font-semibold text-p300">Refer & Earn</h2>
              <p className="text-sm text-p300 mt-1">
                Love the app? Refer a friend!
              </p>
              <button
                onClick={onClose}
                className="btn btn-sm btn-circle absolute right-4 top-6">
                ✕
              </button>
            </div>

            <div className="relative mt-4 h-64 overflow-hidden">
              <div
                className="flex transition-transform duration-300 ease-in-out"
                style={{ transform: `translateX(-${currentIndex * 100}%)` }}>
                {images.map((image, index) => (
                  <div
                    key={index}
                    className="min-w-full h-64 flex justify-center">
                    <img
                      src={image}
                      alt={`Refer and earn ${index + 1}`}
                      className="object-contain h-full"
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-center mt-3 gap-2 h-6">
              {images.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`rounded-full transition-all duration-300 ${
                    currentIndex === index
                      ? "w-2.5 h-2.5 bg-green-500"
                      : "w-1.5 h-1.5 bg-gray-400"
                  }`}
                />
              ))}
            </div>

            <div className="mt-3 bg-p400 text-p300 rounded-xl p-5">
              <h3 className="text-xl font-semibold">{cardHeading} *</h3>
              <p className="mt-3 text-sm">{cardBody}</p>

              <button
                onClick={shareLink}
                className="mt-4 w-full py-2 px-4 bg-swipe-button text-p400 rounded flex items-center justify-center gap-2 font-semibold ">
                <Share size={16} />
                Share link
              </button>

              <button
                onClick={() => setShowTerms(true)}
                className="mt-2 text-xs text-p300 w-full text-left underline">
                *Terms and conditions apply
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col ">
            <div className="flex items-center">
              <button
                onClick={() => setShowTerms(false)}
                className="text-p300 mr-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round">
                  <path d="M19 12H5M12 19l-7-7 7-7" />
                </svg>
              </button>
              <h2 className="text-xl font-semibold text-p300">
                Terms & Conditions
              </h2>
            </div>

            <div className="mt-4 text-p300 text-sm overflow-y-auto max-h-[60vh] no-scrollbar">
              <BulletPoint text="You will receive a sum of ₹1000*." />
              <BulletPoint text="The offer is applicable to all Alice Blue Clients." />
              <BulletPoint text="Under your referral, new customers opened on or after 1st Dec'24 will be eligible for the incentive." />
              <BulletPoint text="This offer is not applicable for Alice Blue Authorized Partners and Business Development Representatives" />
              <BulletPoint text="This offer cannot be combined with any other offer." />
              <BulletPoint text="If your referred client already exists in the Alice Blue database as an existing or potential client, it will not be considered a valid referral." />
              <BulletPoint text="The referral commission will be remitted on the 15th of every month directly into your bank account." />
              <BulletPoint text="A customer will receive a one-time referral cash reward for each referred customer." />
              <BulletPoint text="Alice Blue reserves the right to amend this offer and the terms and conditions at any time without prior notice. The decision of Alice Blue on all matters relating to or in connection with the use of this offer shall be final and binding on all parties concerned. Thee same shall be intimated to the clients through our website and on the registered email address." />
              <BulletPoint text="You agree that the following mentioned activities are expressly prohibited: – Fraud, Attempted Fraud, or Abuse of the Alice Blue Referral offer or the Terms & Conditions. – Selling, Trading, Bartering, or providing anything of value to your Invitee beyond the referral benefits or otherwise using your own program invitations or Commission for promotional purposes." />

              <div className="ml-5 mt-1">
                <SubBulletPoint text="Involve/engage in games/leagues/schemes/competitions etc. which may involve distribution of prize monies/medals/gifts, etc." />
                <SubBulletPoint text="Advertisement of any kind, in any form, and on any medium. Advertisement includes paid media promotions on offline & online mediums including but not limited to Facebook, Twitter, Instagram, YouTube, WhatsApp, etc." />
                <SubBulletPoint text="Any act or omission resulting in damage to Alice blue in any form whatsoever in nature." />
                <SubBulletPoint text="Engaging in any of the above activities will lead to the initiation of legal and criminal action against you and result in:" />

                <div className="ml-5 mt-1">
                  <SubSubBulletPoint text="Immediate termination and disqualification from the Referral Program." />
                  <SubSubBulletPoint text="Forfeiture of existing referral invitations." />
                  <SubSubBulletPoint text="Forfeiture of accrued but not yet received referral rewards." />
                </div>
              </div>

              <div className="ml-2 mt-2 flex">
                <span className="font-bold mr-1">•</span>
                <p>
                  Any grievances related to the above scheme/offer/campaign can
                  be sent to{" "}
                  <a
                    href="mailto:askus@aliceblueindia.com"
                    className="text-blue-500">
                    askus@aliceblueindia.com
                  </a>
                  . Investments in the securities market are subject to market
                  risks, read all related documents carefully before investing
                </p>
              </div>

              <p className="ml-4 mt-2 text-p300">
                *To be eligible to receive the ₹1000 referral bonus, conditions
                apply.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const BulletPoint = ({ text }) => (
  <div className="flex mb-1">
    <span className="font-bold mr-1">•</span>
    <p>{text}</p>
  </div>
);

const SubBulletPoint = ({ text }) => (
  <div className="flex mb-1 ml-3">
    <span className="font-bold mr-1">•</span>
    <p>{text}</p>
  </div>
);

const SubSubBulletPoint = ({ text }) => (
  <div className="flex mb-1 ml-3">
    <span className="font-bold mr-1">•</span>
    <p>{text}</p>
  </div>
);

export default ReferAndEarnModal;
